import { BaseMapLayerManager } from './base-map-layer-manager'
import { BusinessLayerRegistry } from './business-layer-registry'
import { CameraManager } from './camera-manager'
import { MapLoadingStateMachine } from './loading-state-machine'
import {
  AcquirePlatformMapOptions,
  PlatformMapDiagnostics,
  PlatformMapSceneId,
  PlatformMapSceneLease
} from './types'

function envFlag (value: unknown, fallback: boolean) {
  if (value === undefined || value === null || value === '') return fallback
  return String(value).toLowerCase() === 'true'
}

export const persistentMars3dMapEnabled = envFlag(
  import.meta.env.VITE_USE_PERSISTENT_MARS3D_MAP,
  true
)

function managementSceneIdFromStoredMenu (normalizedPath: string): PlatformMapSceneId | null {
  if (typeof window === 'undefined') return null
  try {
    const menuList = JSON.parse(localStorage.getItem('menu_list') || '[]')
    const visit = (nodes: any[]): PlatformMapSceneId | null => {
      for (const node of Array.isArray(nodes) ? nodes : []) {
        const path = `/${String(node?.path || '').replace(/^\/+|\/+$/g, '')}`
        if (path === normalizedPath) {
          const component = String(node?.component || '').replace(/\.vue$/i, '')
          if (component.endsWith('projects/annotationManagement')) return 'annotation-management'
          if (component.endsWith('projects/no-fly-zone')) return 'flight-area'
        }
        const childMatch = visit(node?.children)
        if (childMatch) return childMatch
      }
      return null
    }
    return visit(menuList)
  } catch (error) {
    return null
  }
}

export function platformMapSceneIdFromRoutePath (path: unknown): PlatformMapSceneId | null {
  const normalized = String(path || '').split('?')[0].replace(/\/+$/, '') || '/'
  if (normalized === '/large_screen' || normalized === '/large_screen2') return 'home'
  if (normalized === '/quick-wayline-planning') return 'quick-wayline'
  if (normalized === '/flight_replay' || normalized === '/flight-replay') return 'flight-replay'
  if (normalized === '/route_management' || normalized === '/wayline-panel' || normalized === '/wayline') {
    return 'wayline-library'
  }
  if (normalized === '/annotation_management' || normalized === '/annotation-management') {
    return 'annotation-management'
  }
  if (normalized === '/no-fly-zone' || normalized === '/flight-area' || normalized === '/inspection-area') {
    return 'flight-area'
  }
  return managementSceneIdFromStoredMenu(normalized)
}

class PlatformMapRuntime {
  readonly baseMaps = new BaseMapLayerManager()
  readonly businessLayers = new BusinessLayerRegistry()
  readonly camera = new CameraManager()
  readonly loading = new MapLoadingStateMachine()

  private map: any = null
  private mapRoot: HTMLElement | null = null
  private persistentHost: HTMLElement | null = null
  private activeMountMode: 'persistent' | 'scene-host' = 'persistent'
  private activeSceneId: PlatformMapSceneId | null = null
  private activeSceneLeaseToken: number | null = null
  private sceneLeaseTokenSequence = 0
  private sceneStates = new Map<PlatformMapSceneId, Record<string, any>>()
  private mapCreateCount = 0
  private mapDestroyCount = 0
  private sceneAcquireCount = 0
  private sceneReleaseCount = 0
  private mapInstanceSequence = 0
  // A Mars3D constructor can yield asynchronously. Keep one pending creation
  // for the shared runtime and invalidate it when destroy() tears the runtime
  // down, so an old constructor can never reclaim a newer map root.
  private mapGeneration = 0
  private pendingMapCreation: {
    generation: number
    root: HTMLElement
    promise: Promise<any>
  } | null = null

  private sceneFrames = new Map<PlatformMapSceneId, HTMLCanvasElement>()
  private pendingSourceFrame: HTMLCanvasElement | null = null
  private heldFrame: HTMLCanvasElement | null = null
  private transitionPromise: Promise<void> | null = null
  private resolveTransition: (() => void) | null = null
  private transitionTimeout: number | null = null
  private pendingHideTimer: number | null = null
  private managementPrimitiveAddedCleanup: (() => void) | null = null

  registerPersistentHost (host: HTMLElement | null) {
    this.persistentHost = host
    if (host) host.dataset.platformMapHost = 'true'
    this.updatePersistentHostInteractivity()
    if (host && this.mapRoot && this.activeMountMode === 'persistent' && this.mapRoot.parentElement !== host) {
      host.appendChild(this.mapRoot)
      this.resizeAfterAttach()
    }
  }

  async acquireScene (options: AcquirePlatformMapOptions): Promise<PlatformMapSceneLease> {
    if (!persistentMars3dMapEnabled) {
      throw new Error('常驻 Mars3D Map 功能开关未开启')
    }
    if (!options.host) throw new Error('地图场景缺少挂载容器')
    this.cancelPendingHide()

    const created = !this.map || this.map?.isDestroyed?.()
    const previousSceneId = this.activeSceneId
    const leaseToken = ++this.sceneLeaseTokenSequence
    const mountMode = options.mountMode === 'scene-host' ? 'scene-host' : 'persistent'
    const targetHost = mountMode === 'scene-host'
      ? options.host
      : (this.persistentHost || options.host)
    const switchingScene = !created && Boolean(previousSceneId) && previousSceneId !== options.sceneId
    if (switchingScene) {
      // Vue may mount the destination route before deactivating the source.
      // Transfer controller ownership while the fixed Canvas stays mounted.
      this.camera.cancel(this.map?.viewer)
      this.activeSceneId = null
      this.activeSceneLeaseToken = null
      this.sceneReleaseCount += 1
    }
    if (created) {
      await this.ensureMapCreated(options, targetHost)
    } else if (this.mapRoot && this.mapRoot.parentElement !== targetHost) {
      targetHost.appendChild(this.mapRoot)
      this.resizeAfterAttach()
    }

    if (!this.mapRoot || !this.map) throw new Error('常驻地图实例创建失败')
    this.activeMountMode = mountMode
    this.mapRoot.style.display = 'block'
    this.mapRoot.style.visibility = 'visible'
    this.activeSceneId = options.sceneId
    this.activeSceneLeaseToken = leaseToken
    this.applyManagementModelVisibility(options.sceneId)
    this.restoreThreeDimensionalSceneForStandardPage(options.sceneId)
    this.applyViewerSettings(options.viewerSettings)
    this.updatePersistentHostInteractivity()
    this.sceneAcquireCount += 1
    this.businessLayers.activateScene(options.sceneId)
    this.disableMars3dContextMenu()
    this.updateRootDiagnostics()
    // The persistent root can already be attached but still carry the drawing
    // buffer size of a modal scene-host. Always resize after making it visible;
    // checking only for creation/reparenting misses that close -> page acquire.
    this.resizeAfterAttach()
    if (!created) this.showHeldSceneFrame(options.sceneId)

    const sceneState = this.getSceneState(options.sceneId)
    let released = false
    return {
      map: this.map,
      viewer: this.map.viewer,
      created,
      sceneState,
      isActive: () => this.activeSceneId === options.sceneId && this.activeSceneLeaseToken === leaseToken,
      release: () => {
        if (released) return
        released = true
        this.releaseScene(options.sceneId, leaseToken)
      }
    }
  }

  getSceneState (sceneId: PlatformMapSceneId) {
    let state = this.sceneStates.get(sceneId)
    if (!state) {
      state = {}
      this.sceneStates.set(sceneId, state)
    }
    return state
  }

  markReady (currentViewStable = false) {
    this.loading.transition('READY', true)
    this.updateRootDiagnostics()
    if (currentViewStable) this.finishSceneTransition()
  }

  async prepareSceneTransition () {
    if (!this.map || this.map?.isDestroyed?.() || !this.activeSceneId) return
    if (this.transitionPromise) return
    // The Viewer and Canvas are persistent. Keep displaying that same Canvas
    // while Vue swaps business panels; copying a WebGL buffer with
    // preserveDrawingBuffer=false can produce a black transition frame.
    this.cancelPendingHide()
    this.finishSceneTransition(true)
    this.pendingSourceFrame = null
    this.sceneFrames.clear()
    // Keep releaseScene() from hiding the shared Canvas during the brief gap
    // between the source route unmounting and the destination route acquiring
    // the same Viewer. acquireScene() completes this handoff immediately.
    this.transitionPromise = new Promise(resolve => { this.resolveTransition = resolve })
    this.transitionTimeout = window.setTimeout(() => this.finishSceneTransition(), 2000)
    this.updateRootDiagnostics()
  }

  waitForSceneTransition () {
    return this.transitionPromise || Promise.resolve()
  }

  releaseScene (sceneId: PlatformMapSceneId, leaseToken: number) {
    if (this.activeSceneId !== sceneId || this.activeSceneLeaseToken !== leaseToken) return
    this.activeSceneId = null
    this.activeSceneLeaseToken = null
    this.activeMountMode = 'persistent'
    if (this.persistentHost && this.mapRoot && this.mapRoot.parentElement !== this.persistentHost) {
      this.persistentHost.appendChild(this.mapRoot)
      this.resizeAfterAttach()
    }
    this.clearManagementPrimitiveGuard()
    this.updatePersistentHostInteractivity()
    this.sceneReleaseCount += 1
    this.camera.cancel(this.map?.viewer)
    this.updateRootDiagnostics()
    // Non-map pages share the same transparent application shell. Leaving the
    // persistent Canvas visible with no active scene leaks the homepage's last
    // rendered frame (including suitable-airspace tint) beneath those pages.
    // A prepared home <-> quick-wayline transition owns a held frame and must
    // stay visible; all other ownerless canvases are hidden after the current
    // Vue route-change tick. A destination scene acquired in that tick cancels
    // this hide before it runs.
    this.scheduleHidePersistentCanvasIfInactive()
  }

  destroy () {
    // invalidate a constructor which is still awaiting a Mars3D Map. Its
    // completion handler destroys the detached result instead of restoring it.
    this.mapGeneration += 1
    this.pendingMapCreation = null
    this.cancelPendingHide()
    this.finishSceneTransition(true)
    if (this.map && !this.map?.isDestroyed?.()) {
      try {
        this.map.destroy()
        this.mapDestroyCount += 1
      } catch (error) {
        console.warn('销毁平台地图失败:', error)
      }
    }
    this.resetDestroyedMap()
    this.sceneStates.clear()
    this.sceneFrames.clear()
    this.pendingSourceFrame = null
    this.baseMaps.clear()
    this.loading.transition('IDLE', true)
    this.updatePersistentHostInteractivity()
  }

  diagnostics (): PlatformMapDiagnostics {
    return {
      enabled: persistentMars3dMapEnabled,
      activeSceneId: this.activeSceneId,
      loadingState: this.loading.state,
      mapCreateCount: this.mapCreateCount,
      mapDestroyCount: this.mapDestroyCount,
      sceneAcquireCount: this.sceneAcquireCount,
      sceneReleaseCount: this.sceneReleaseCount,
      canvasCount: this.mapRoot?.querySelectorAll('canvas').length || 0
    }
  }

  private resizeAfterAttach () {
    window.requestAnimationFrame(() => {
      if (!this.map || this.map?.isDestroyed?.()) return
      try { this.map.resize?.() } catch (error) { /* Mars3D versions differ */ }
      try { this.map.viewer?.resize?.() } catch (error) { /* viewer may be rendering */ }
      this.map.viewer?.scene?.requestRender?.()
      this.updateRootDiagnostics()
    })
  }

  private async ensureMapCreated (options: AcquirePlatformMapOptions, targetHost: HTMLElement) {
    let pendingCreation = this.pendingMapCreation
    if (!pendingCreation) {
      // A Viewer may already have been destroyed externally. Remove its stale
      // root before allocating the one root owned by the new creation.
      if (this.map || this.mapRoot) this.resetDestroyedMap()

      const root = document.createElement('div')
      root.className = 'platform-persistent-map__canvas'
      Object.assign(root.style, {
        position: 'absolute',
        inset: '0',
        width: '100%',
        height: '100%',
        overflow: 'hidden',
        zIndex: '0',
        isolation: 'isolate'
      })
      this.mapRoot = root
      targetHost.appendChild(root)
      this.loading.transition('BOOTING', true)

      const generation = this.mapGeneration
      pendingCreation = {
        generation,
        root,
        promise: Promise.resolve(null)
      }
      this.pendingMapCreation = pendingCreation
      pendingCreation.promise = Promise.resolve()
        .then(() => options.createMap(root))
        .then(map => {
          if (!map) throw new Error('常驻地图实例创建失败')
          if (this.pendingMapCreation !== pendingCreation || this.mapGeneration !== generation) {
            // destroy() (or a later runtime generation) won the race. Mars3D
            // may still finish constructing its Viewer after its root was
            // detached, so dispose it here to avoid a hidden WebGL context.
            this.mapCreateCount += 1
            this.destroyMapInstance(map)
            throw new Error('常驻地图实例初始化已取消')
          }
          this.map = map
          this.pendingMapCreation = null
          this.disableMars3dContextMenu()
          this.mapCreateCount += 1
          this.mapInstanceSequence += 1
          this.loading.transition('INITIAL_IMAGERY')
          return map
        })
        .catch(error => {
          // Only the creation which still owns the current root may reset it.
          // A stale failure must not remove a root created by a newer caller.
          if (this.pendingMapCreation === pendingCreation && this.mapGeneration === generation) {
            this.pendingMapCreation = null
            this.loading.transition('ERROR', true)
            if (this.mapRoot === root && !this.map) this.resetDestroyedMap()
          }
          throw error
        })
    }

    const map = await pendingCreation.promise
    if (this.mapGeneration !== pendingCreation.generation || this.map !== map || map?.isDestroyed?.()) {
      throw new Error('常驻地图实例初始化已取消')
    }
    return map
  }

  private destroyMapInstance (map: any) {
    if (!map || map?.isDestroyed?.() || typeof map.destroy !== 'function') return
    try {
      map.destroy()
      this.mapDestroyCount += 1
    } catch (error) {
      console.warn('销毁过期平台地图失败:', error)
    }
  }

  private applyViewerSettings (settings = {} as NonNullable<AcquirePlatformMapOptions['viewerSettings']>) {
    const viewer = this.map?.viewer
    if (!viewer || viewer?.isDestroyed?.()) return
    if (Number.isFinite(settings.targetFrameRate)) viewer.targetFrameRate = settings.targetFrameRate
    if (Number.isFinite(settings.resolutionScale)) viewer.resolutionScale = settings.resolutionScale
    if (typeof settings.requestRenderMode === 'boolean' && viewer.scene) {
      viewer.scene.requestRenderMode = settings.requestRenderMode
    }
    const controller = viewer.scene?.screenSpaceCameraController
    // Reapply on scene reuse: terrain and collision-enabled 3D Tiles both
    // contribute to Cesium's camera surface-height constraint.
    if (controller) controller.enableCollisionDetection = true
    if (controller && Number.isFinite(settings.minimumZoomDistance)) {
      controller.minimumZoomDistance = settings.minimumZoomDistance
    }
    if (controller && Number.isFinite(settings.maximumZoomDistance)) {
      controller.maximumZoomDistance = settings.maximumZoomDistance
    }
    viewer.scene?.requestRender?.()
  }

  private restoreThreeDimensionalSceneForStandardPage (sceneId: PlatformMapSceneId) {
    if (this.isManagementTwoDimensionalScene(sceneId)) return
    const scene = this.map?.viewer?.scene
    // Cesium SceneMode.SCENE3D is numeric value 3. A zero-duration morph
    // restores the shared Viewer after leaving a true-2D management page
    // without displaying Cesium's default globe transition animation.
    if (scene && scene.mode !== 3 && typeof scene.morphTo3D === 'function') {
      scene.morphTo3D(0)
      scene.requestRender?.()
    }
  }

  private applyManagementModelVisibility (sceneId: PlatformMapSceneId) {
    this.clearManagementPrimitiveGuard()
    if (!this.isModelFreeManagementScene(sceneId)) return
    const scene = this.map?.viewer?.scene
    const primitives = scene?.primitives
    if (!primitives) return

    const managedTilesets = new Set<any>()
    const registerTilesets = (primitive: any) => {
      if (!primitive) return
      const constructorName = String(primitive?.constructor?.name || '')
      if (primitive.__platformHdModel || /(?:Cesium)?3DTileset/i.test(constructorName)) {
        managedTilesets.add(primitive)
        primitive.show = false
        if ('preloadWhenHidden' in primitive) primitive.preloadWhenHidden = false
      }
      if (Array.isArray(primitive?._primitives)) primitive._primitives.forEach(registerTilesets)
    }
    const enforceManagementModelVisibility = () => {
      if (this.activeSceneId !== sceneId) return
      managedTilesets.forEach(tileset => {
        try {
          if (!tileset || tileset.isDestroyed?.()) {
            managedTilesets.delete(tileset)
            return
          }
          tileset.show = false
          if ('preloadWhenHidden' in tileset) tileset.preloadWhenHidden = false
        } catch (error) {
          // Cesium keeps destroyed primitives addressable briefly; do not hold
          // them for the rest of a long-lived management scene.
          managedTilesets.delete(tileset)
        }
      })
    }
    for (let index = 0; index < Number(primitives.length || 0); index += 1) {
      try { registerTilesets(primitives.get(index)) } catch (error) { /* collection changed while scanning */ }
    }

    const hideAddedPrimitive = (primitive: any) => {
      if (this.activeSceneId !== sceneId) return
      registerTilesets(primitive)
      // Some loaders set `show` immediately after adding the primitive. Recheck
      // once after their synchronous work so management scenes stay model-free.
      window.queueMicrotask(() => {
        if (this.activeSceneId === sceneId) registerTilesets(primitive)
      })
      window.requestAnimationFrame(() => {
        if (this.activeSceneId === sceneId) {
          registerTilesets(primitive)
          scene.requestRender?.()
        }
      })
    }
    const cleanupListeners: Array<() => void> = []
    const addCleanupListener = (event: any, listener: (...args: any[]) => void) => {
      try {
        const remove = event?.addEventListener?.(listener)
        if (typeof remove === 'function') cleanupListeners.push(remove)
        else if (typeof event?.removeEventListener === 'function') {
          cleanupListeners.push(() => event.removeEventListener(listener))
        }
      } catch (error) { /* older Cesium builds may not expose this event */ }
    }
    addCleanupListener(primitives.primitiveAdded, hideAddedPrimitive)
    addCleanupListener(scene.preRender, enforceManagementModelVisibility)
    this.managementPrimitiveAddedCleanup = () => {
      cleanupListeners.forEach(remove => {
        try { remove() } catch (error) { /* viewer may already be destroyed */ }
      })
    }
    scene.requestRender?.()
  }

  private isManagementTwoDimensionalScene (sceneId: PlatformMapSceneId) {
    return sceneId === 'annotation-management' ||
      sceneId === 'flight-area' ||
      sceneId === 'region-location-picker'
  }

  private isModelFreeManagementScene (sceneId: PlatformMapSceneId) {
    return sceneId === 'flight-area' ||
      sceneId === 'region-location-picker'
  }

  private clearManagementPrimitiveGuard () {
    try { this.managementPrimitiveAddedCleanup?.() } catch (error) { /* viewer may already be destroyed */ }
    this.managementPrimitiveAddedCleanup = null
  }

  private disableMars3dContextMenu () {
    const map = this.map
    if (!map || typeof map.unbindContextMenu !== 'function') return
    try {
      map.unbindContextMenu()
    } catch (error) {
      console.warn('关闭 Mars3D 右键菜单失败:', error)
    }
  }

  private updatePersistentHostInteractivity () {
    if (!this.persistentHost) return
    const canvasUsesPersistentHost = this.mapRoot?.parentElement === this.persistentHost
    this.persistentHost.style.pointerEvents = this.activeSceneId && canvasUsesPersistentHost ? 'auto' : 'none'
  }

  private captureCurrentFrame () {
    const sourceCanvas = this.map?.viewer?.scene?.canvas as HTMLCanvasElement | undefined
    if (!sourceCanvas || sourceCanvas.width <= 0 || sourceCanvas.height <= 0) return null
    try {
      const frame = document.createElement('canvas')
      frame.width = sourceCanvas.width
      frame.height = sourceCanvas.height
      frame.getContext('2d')?.drawImage(sourceCanvas, 0, 0)
      return frame
    } catch (error) {
      return null
    }
  }

  private storeCurrentFrame (sceneId: PlatformMapSceneId) {
    const frame = this.captureCurrentFrame()
    if (!frame) return
    this.sceneFrames.set(sceneId, frame)
    this.pendingSourceFrame = frame
  }

  private showHeldSceneFrame (sceneId: PlatformMapSceneId, preservePendingSource = false) {
    this.finishSceneTransition(true)
    if (!this.mapRoot) return
    const source = this.sceneFrames.get(sceneId) || this.pendingSourceFrame
    if (!preservePendingSource) this.pendingSourceFrame = null
    if (!source) return

    const overlay = document.createElement('canvas')
    overlay.className = 'platform-persistent-map__held-frame'
    overlay.width = source.width
    overlay.height = source.height
    Object.assign(overlay.style, {
      position: 'absolute',
      inset: '0',
      width: '100%',
      height: '100%',
      zIndex: '2',
      pointerEvents: 'none',
      opacity: '1',
      transition: 'opacity 80ms linear'
    })
    overlay.getContext('2d')?.drawImage(source, 0, 0)
    this.mapRoot.appendChild(overlay)
    this.heldFrame = overlay
    this.transitionPromise = new Promise(resolve => { this.resolveTransition = resolve })
    // Keep the previous complete frame until the destination base imagery is
    // actually ready. This is only a deadlock guard; the normal path calls
    // finishSceneTransition() immediately from markReady().
    this.transitionTimeout = window.setTimeout(() => this.finishSceneTransition(), 8000)
    this.updateRootDiagnostics()
  }

  private finishSceneTransition (immediate = false) {
    if (this.transitionTimeout !== null) window.clearTimeout(this.transitionTimeout)
    this.transitionTimeout = null
    const overlay = this.heldFrame
    const resolve = this.resolveTransition
    this.heldFrame = null
    this.resolveTransition = null
    this.transitionPromise = null
    const complete = () => {
      try { overlay?.remove() } catch (error) { /* overlay may already be detached */ }
      resolve?.()
      // The destination route can fail or be interrupted before acquiring its
      // scene. Do not leave the source Canvas covering the application shell.
      this.scheduleHidePersistentCanvasIfInactive()
      this.updateRootDiagnostics()
    }
    if (!overlay || immediate) {
      complete()
      return
    }
    overlay.style.pointerEvents = 'none'
    overlay.style.opacity = '0'
    window.setTimeout(complete, 80)
  }

  private updateRootDiagnostics () {
    if (!this.mapRoot) return
    const diagnostics = this.diagnostics()
    this.mapRoot.dataset.mapInstanceId = String(this.mapInstanceSequence)
    this.mapRoot.dataset.mapCreateCount = String(diagnostics.mapCreateCount)
    this.mapRoot.dataset.mapDestroyCount = String(diagnostics.mapDestroyCount)
    this.mapRoot.dataset.sceneAcquireCount = String(diagnostics.sceneAcquireCount)
    this.mapRoot.dataset.sceneReleaseCount = String(diagnostics.sceneReleaseCount)
    this.mapRoot.dataset.activeScene = String(diagnostics.activeSceneId || '')
    this.mapRoot.dataset.loadingState = diagnostics.loadingState
    this.mapRoot.dataset.host = this.mapRoot.parentElement?.dataset.platformMapHost === 'true'
      ? 'persistent'
      : 'route'
    this.mapRoot.dataset.heldFrame = this.heldFrame ? 'true' : 'false'
    const pitchRadians = Number(this.map?.viewer?.camera?.pitch)
    if (Number.isFinite(pitchRadians)) {
      this.mapRoot.dataset.cameraPitchDegrees = String(pitchRadians * 180 / Math.PI)
    }
  }

  private resetDestroyedMap () {
    this.cancelPendingHide()
    this.clearManagementPrimitiveGuard()
    try { this.mapRoot?.remove() } catch (error) { /* root may already be detached */ }
    this.map = null
    this.mapRoot = null
    this.activeSceneId = null
    this.activeSceneLeaseToken = null
    this.activeMountMode = 'persistent'
  }

  private cancelPendingHide () {
    if (this.pendingHideTimer !== null) window.clearTimeout(this.pendingHideTimer)
    this.pendingHideTimer = null
  }

  private scheduleHidePersistentCanvasIfInactive () {
    if (this.activeSceneId || this.transitionPromise || this.heldFrame || !this.mapRoot) return
    this.cancelPendingHide()
    this.pendingHideTimer = window.setTimeout(() => {
      this.pendingHideTimer = null
      if (this.activeSceneId || this.transitionPromise || this.heldFrame || !this.mapRoot) return
      this.mapRoot.style.display = 'none'
      this.mapRoot.style.visibility = 'hidden'
      this.updatePersistentHostInteractivity()
      this.updateRootDiagnostics()
    }, 0)
  }
}

export const platformMapRuntime = new PlatformMapRuntime()

if (typeof window !== 'undefined') {
  ;(window as any).__PLATFORM_MAP_RUNTIME__ = platformMapRuntime
}
